import { LayoutDashboard, Users, Target, Megaphone, Brain, Lightbulb, Clipboard, FileText, Zap, GraduationCap, BookOpen, Library, Activity, ClipboardList, Film, Sparkles, Briefcase, FlaskConical, CalendarDays, MessageSquareText, Workflow, Clapperboard } from "lucide-react";
import { PD_ITEMS, TEAM_ITEMS, INSIGHTS_ITEMS, TEAM_INSIGHTS_ITEMS, XO_ITEMS, PLANS_ITEMS, MOVEMENT_ITEMS, WORKOUTS_ITEMS, MASTER_ITEMS } from "../../mockData";

export const NAV = [
  { id:"dashboard", label:"Home",             Icon:LayoutDashboard, group:"main" },
  { id:"portal",    label:"My Portal",        Icon:Briefcase,       group:"main" },
  { id:"community", label:"Community",         Icon:Users,           group:"main" },
  // Calls used to live only on the Community page, below the tag chips and
  // the trending posts, plus a sidebar banner in the final thirty minutes.
  // A coach wondering at 5pm whether there was a call that night had
  // nowhere to look, and one of them told us so.
  { id:"calls",     label:"Calls",             Icon:CalendarDays,    group:"main" },
  // BAM IQ's full page. No sidebar row (D85/J1: a top-bar entry, no
  // permanent nav tab at first): the top-bar button opens the panel, and the
  // panel opens this page. Only where bamiqAvailable() says so: the accounts
  // on BAM IQ's list (D145), on the web, never in the iPhone app (D106).
  { id:"iq",        label:"BAM IQ",            Icon:MessageSquareText, group:"main", hidden:true },
  // Advantage Flow: the season, the games, the numbers and the staff's
  // definitions (Cavaliers, 2026-10-20). Its own row under Coach since
  // 2026-10-22 (Coleman: "a completely different feature/nav than film
  // study... gated for people on nav who have access to it"). Since
  // 2026-11-03 ("make the afs sidebar nav visible to everyone with just a
  // very basic preview / locked screen") it's in every signed-in account's
  // sidebar on the web: with access (asked through /api/afs-narrate) it
  // opens the app, without it a locked preview. Never in the iOS app.
  // BAMFull's resolver does the gating (afsPitch.afsInSidebar).
  { id:"afs",       label:"Advantage Tracking",Icon:Workflow,        group:"main" },
  // The one surface designed to be opened every day. It sits in the main
  // group for that reason, and the sidebar drops it while the lab is gated.
  { id:"learn",     label:"Learning Lab",      Icon:FlaskConical,    group:"main" },
  // Insights used to be two items: one buried in Player Development, one in
  // Team Coaching, with the same label and no way to get from one to the
  // other. They are one section now. `ti` is still a real route (its own
  // content, its own lessons rail, and every link ever sent still works) but
  // it is reached by the toggle at the top of the page rather than by a
  // second sidebar row reading "Insights", so it is marked hidden.
  { id:"insights",  label:"Insights",          Icon:Brain,           group:"main" },
  { id:"ti",        label:"Team Insights",     Icon:Lightbulb,       group:"main", hidden:true },
  // Player Development (collapsible)
  { id:"pd",        label:"Drills & Games",    short:"Drills", Icon:Target,          group:"pd",   parent:"pd" },
  { id:"movement",  label:"Movement Drills",   Icon:Activity,        group:"pd",   parent:"pd" },
  { id:"workouts",  label:"Workout Examples",  Icon:Zap,             group:"pd",   parent:"pd" },
  // Team Coaching (collapsible)
  { id:"team",      label:"Drills & Games",    short:"Drills", Icon:Megaphone,       group:"team", parent:"team" },
  { id:"xo",        label:"X & O Breakdowns",  Icon:Clipboard,       group:"team", parent:"team" },
  { id:"playbook",  label:"Playbook",          Icon:BookOpen,        group:"team", parent:"team" },
  // Content — standalone
  { id:"master",    label:"Masterclasses",     Icon:GraduationCap,   group:"content" },
  { id:"resources", label:"Resources",         Icon:Library,         group:"main" },
  // Toolbox (app store)
  { id:"plans",     label:"Training Plans",    Icon:FileText,        group:"toolbox" },
  { id:"dev-studio",label:"My Players",        Icon:ClipboardList,   group:"toolbox" },
  // BAM Film Studio — beta-gated. The sidebar filter strips this item
  // for non-allowlisted accounts (see BAMFull, where the toolbox NAV
  // group filter checks `beta` against isAllowedBetaUser).
  { id:"film",      label:"BAM Film Studio",   Icon:Film,            group:"toolbox" },
  // BAM Consulting — beta-gated marketing surface. Org dashboard ("/org")
  // is conditionally shown via the sidebar in BAMFull when the user has
  // an org membership (handled there, not here, since it's profile-keyed).
  // Hidden: reached from Resources, not the nav (Coleman, 2026-10-10).
  { id:"consulting", label:"BAM Consulting",   Icon:Sparkles,        group:"toolbox", beta:true, hidden:true },
  { id:"shotflow",  label:"ShotFlow",          Icon:Target,          group:"toolbox", comingSoon:true },
  // Advantage Flow used to live here as its own toolbox item. It's now
  // a tagging mode inside BAM Film Studio (which itself is beta-gated
  // to allowlist accounts in the Toolbox). Don't surface AFS as a
  // separate sidebar item — it'd make coaches expect a separate page.
  { id:"shot-prog", label:"Shooting Programs", Icon:Activity,        group:"toolbox", comingSoon:true },
  // Film Desk: the shared clip library. Invite-only (admins, remote interns and contributors),
  // shown as a sidebar tile in BAMFull, not a public row. Hidden keeps it off
  // the sidebar list while routable; MAP_EXEMPT covers the tour test.
  { id:"filmdesk", label:"Film Desk",         Icon:Clapperboard,    group:"toolbox", hidden:true },
];

// Section ids are database values ("pd", "ti", "xo"). They are not words, and
// they were leaking into coach-facing surfaces — a drill card reading
// "pd · General" tells a coach nothing and looks like a bug. Anywhere a
// section is shown to a coach, it goes through this.
//
// These are shorter than the CONTENT titles on purpose: a title heads a page
// ("Player Development Drills & Games"), a label sits inline under a drill
// name where every extra word is noise.
export const SECTION_LABEL = {
  pd:        "Player Development",
  team:      "Team Coaching",
  insights:  "Player Development Insights",
  ti:        "Team Insights",
  xo:        "X & O Breakdowns",
  plans:     "Training Plans",
  movement:  "Movement Drills",
  workouts:  "Workout Examples",
  master:    "Masterclasses",
  pro:       "Pro Development",
  my:        "My Drills",
};

// Falls back to whatever it was given, so an unknown or future section shows
// something rather than vanishing from the line it was part of.
export const sectionLabel = (id) => SECTION_LABEL[id] || id || "";

// Thumb colors per section
export const SECTION_COLORS = {
  pd:       ["#3D6B5E","#4A5E7A","#6B4A5E","#5E5A3D","#3D5E6B","#5E3D4A","#4A6B4A","#7A5E3D"],
  team:     ["#5E3D3D","#3D4A6B","#5E5E3D","#3D6B4A","#6B3D5E","#4A6B5E","#5E4A3D","#3D5E5E"],
  insights: ["#4A3D6B","#3D6B5E","#6B5E3D","#3D4A5E","#5E3D6B","#6B4A3D","#3D6B6B","#5E5E4A"],
  ti:       ["#3D5E4A","#5E3D5E","#4A5E3D","#6B4A4A","#3D3D6B","#5E6B3D","#4A3D5E","#6B5E4A"],
  xo:       ["#5E4A3D","#3D5E6B","#6B3D3D","#4A6B3D","#3D4A6B","#6B5E3D","#3D6B4A","#5E3D6B"],
  plans:    ["#4A6B3D","#5E3D4A","#3D5E5E","#6B4A5E","#3D6B3D","#5E5E3D","#4A3D6B","#6B3D5E"],
  movement: ["#5E6B4A","#3D6B4A","#4A6B5E","#6B5E3D","#3D4A5E","#5E4A6B","#6B3D3D","#4A3D3D"],
  workouts: ["#6B3D3D","#3D6B4A","#5E4A6B","#4A6B5E","#6B5E3D","#3D4A5E","#5E6B4A","#4A3D3D"],
  master:   ["#5E3D5E","#3D5E3D","#6B4A3D","#4A4A6B","#3D6B5E","#5E3D3D","#6B6B3D","#3D4A4A"],
  my:       ["#6B5E3D","#3D6B6B","#5E3D6B","#4A6B3D","#6B3D4A","#3D5E4A","#5E6B3D","#4A3D6B"],
  // Deeper and warmer than the library sections on purpose: the
  // collection should not look like another shelf of the same thing.
  pro:      ["#4A3B28","#2E3A44","#432F2F","#33422F","#3E2F44","#2F4244","#44392F","#2F3348"],
};

// Content data per section
// Taken off the shelf without being deleted. A shelved item keeps its card
// copy, its key points and its module, and simply stops being listed: it is
// for a masterclass that has been superseded or is being held back, where
// removing the entry would throw away writing we want back later.
//
// Every surface that lists content filters on it. That is three places, not
// one, because CONTENT gets flattened for the dashboard and the practice
// planner as well as rendered on the shelf, and shelvedGate.test.js fails if
// a new consumer forgets. The same shape of bug as a drill being held from
// the library and still turning up in search.
export const notShelved = (item) => !item?.shelved;

export const CONTENT = {
  pd: {
    title:"Player Development Drills & Games",
    desc:"Individual player development drills, constraint-based games, and skill-building progressions.",
    filters:["All","Shooting","Finishing","Ballhandling","Attacking","Tactical & Passing"],
    // Orthogonal format axis — matches drills.format ("on-air" / "ssg").
    // Rendered as a separate chip group in ContentPage and AND-stacked
    // with the category + level filters above.
    formats:["on-air","ssg"],
    items: PD_ITEMS,
  },
  team: {
    title:"Team Drills & Games",
    desc:"Group drills, small-sided games, and team-building activities for practice environments.",
    // Filter chips switched to the curated TEAM_CATEGORIES taxonomy
    // (kebab-case ids stored in drills.categories text[]). Display labels
    // come from TEAM_CATEGORY_LABEL — ContentPage maps the id back to a
    // label when rendering. Multi-select chips work via the existing
    // activeFilters Set; matching is `item.categories.includes(filterId)`.
    filters:["All","snapshot-games","trigger-development","transition","defense","half-court-offense","scrimmage","spacing"],
    filterSource: "categories",
    // Orthogonal format axis — same as `pd`, matched against drills.format.
    formats:["on-air","ssg"],
    items: TEAM_ITEMS,
  },
  insights: {
    title:"Player Development Insights",
    desc:"Research-backed reads, frameworks, and breakdowns on individual player development philosophy.",
    filters:["All","Development","Psychology","Methodology","Culture","Research"],
    items: INSIGHTS_ITEMS,
  },
  ti: {
    title:"Team Insights",
    desc:"Team-level strategy, system design, and culture-building frameworks for coaches.",
    filters:["All","Strategy","Culture","Systems","Communication","Analytics"],
    items: TEAM_INSIGHTS_ITEMS,
  },
  xo: {
    title:"X & O Breakdowns",
    desc:"Play diagrams, set play libraries, and strategic breakdowns from professional and international basketball.",
    filters:["All","Sets","ATO","BLOB","SLOB","Half Court"],
    items: XO_ITEMS,
  },
  plans: {
    title:"Training Plans",
    desc:"Ready-to-run training plans for different levels, settings, and time constraints.",
    filters:["All","60 min","90 min","120 min","Youth","Pro"],
    items: PLANS_ITEMS,
  },
  movement: {
    title:"Movement Drills",
    desc:"Warmup exercises, movement patterns, and athletic development drills for training sessions.",
    filters:["All","Warmup","Plyometrics","Coordination","Mobility","Physicality"],
    items: MOVEMENT_ITEMS,
  },
  workouts: {
    title:"Workout Examples",
    desc:"Complete on-court workouts for individual and group training at every level.",
    filters:["All","Individual","Group"],
    items: WORKOUTS_ITEMS,
  },
  // Pro Development — a curated collection, not a section. Its items come
  // from `drills.collections @> {pro}` across every section (useDrills
  // special-cases the id), so the filter chips here are the KIND of
  // thing rather than a skill or a tactical category: a coach landing
  // here wants to know whether they're looking at a whole workout or a
  // single drill first, and what it's about second.
  pro: {
    title:"Pro Development",
    desc:"A collection of drills, games, and videos that we've identified as most applicable to the professional level.",
    filters:["All","workouts","pd","insights","master"],
    filterSource: "section",
    items: [],
  },
  master: {
    title:"Masterclasses",
    desc:"Deep dive content and guest coaches from around the world.",
    filters:["All","Player Development","Coaching Concepts","Team Coaching","Guest"],
    items: MASTER_ITEMS,
  },
};
